import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { ProjectLocationPlanCameraService } from '../../services/project-location-plan-camera.service';
import { ProjectLocationPlanCameraActions } from '../state/project-location-plan-camera/project-location-plan-camera.actions';
import { MsgBoxType, ToastService } from '@ui';
import { TranslateService } from '@ngx-translate/core';
import { ProjectLocationPlanCameraModel } from '../models/project-location-plan-camera.model';

export const loadProjectLocationPlanCameras = createEffect(
  (actions$ = inject(Actions), cameraService = inject(ProjectLocationPlanCameraService)) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCameraActions.loadProjectLocationPlanCameras),
      switchMap(({ projectId }) => {
        return cameraService.getAll(projectId).pipe(
          map((cameras) =>
            ProjectLocationPlanCameraActions.loadProjectLocationPlanCamerasSuccess(
              { cameras },
            ),
          ),
          catchError(() =>
            of(ProjectLocationPlanCameraActions.loadProjectLocationPlanCamerasFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
);

export const updateProjectLocationPlanCameras = createEffect(
  (actions$ = inject(Actions), cameraService = inject(ProjectLocationPlanCameraService)) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCameraActions.updateProjectLocationPlanCameras),
      switchMap(({ projectId, cameraIds, changes }) => {
        const cameras: ProjectLocationPlanCameraModel.API.BulkUpdateCameraDto = {
          cameras: cameraIds.map((id) => {
            return {
              cameraId: id,
              data: { ...changes }
            }
          })
        };

        return cameraService.updateMany(projectId, cameras).pipe(
          map(() =>
            ProjectLocationPlanCameraActions.updateProjectLocationPlanCamerasSuccess(
              { cameraIds, changes },
            ),
          ),
          catchError(() =>
            of(ProjectLocationPlanCameraActions.updateProjectLocationPlanCamerasFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
)

export const updateProjectLocationPlanCamera = createEffect(
  (
    actions$ = inject(Actions),
    cameraService = inject(ProjectLocationPlanCameraService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCameraActions.updateProjectLocationPlanCamera),
      switchMap(({ cameraId, changes, showToast }) => {
        return cameraService.updateOne(cameraId, changes).pipe(
          tap(() => {
            if (showToast === false) {
              return;
            }

            toastService.showCustomAlert(
              translateService.instant('CAMERA_MODEL_DIALOG.TOASTS.UPDATE_SUCCESS') as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((camera) =>
            ProjectLocationPlanCameraActions.updateProjectLocationPlanCameraSuccess(
              { camera },
            ),
          ),
          catchError(() =>
            of(ProjectLocationPlanCameraActions.updateProjectLocationPlanCameraFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
)

export const loadPlanProjectCameras = createEffect(
  (actions$ = inject(Actions), cameraService = inject(ProjectLocationPlanCameraService)) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCameraActions.loadPlanProjectCameras),
      switchMap(({ projectId, locationId, planId }) => {
        return cameraService.getPlanCameras(projectId, locationId, planId).pipe(
          map((cameras) =>
            ProjectLocationPlanCameraActions.loadPlanProjectCamerasSuccess(
              { cameras },
            ),
          ),
          catchError(() =>
            of(ProjectLocationPlanCameraActions.loadPlanProjectCamerasFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
)

export const savePlanProjectCamera = createEffect(
  (
    actions$ = inject(Actions),
    cameraService = inject(ProjectLocationPlanCameraService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCameraActions.savePlanProjectCamera),
      switchMap(({ projectId, locationId, planId, cameraData }) => {
        return cameraService.saveCamera(projectId, locationId, planId, cameraData).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('CAMERA_MODEL_DIALOG.TOASTS.CREATE_SUCCESS') as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((camera) =>
            ProjectLocationPlanCameraActions.savePlanProjectCameraSuccess(
              { camera },
            ),
          ),
          catchError(() =>
            of(ProjectLocationPlanCameraActions.savePlanProjectCameraFail()),
          ),
        );
      }),
    );
  },
  { functional: true },
)

export const copyCamera = createEffect(
  (
    actions$ = inject(Actions),
    cameraService = inject(ProjectLocationPlanCameraService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCameraActions.copyCamera),
      switchMap(({ cameraId }) => {
        return cameraService.copyCamera(cameraId).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('CAMERA_MODEL_DIALOG.TOASTS.COPY_SUCCESS') as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((camera) =>
            ProjectLocationPlanCameraActions.copyCameraSuccess(
              { camera },
            ),
          ),
          catchError(() => {
            toastService.showCustomAlert(
              translateService.instant('CAMERA_MODEL_DIALOG.TOASTS.COPY_FAIL') as string,
              MsgBoxType.SUCCESS,
            );
            return of(ProjectLocationPlanCameraActions.copyCameraFail())
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const deleteCamera = createEffect(
  (
    actions$ = inject(Actions),
    cameraService = inject(ProjectLocationPlanCameraService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCameraActions.deleteCamera),
      switchMap(({ cameraId }) => {
        return cameraService.deleteCamera(cameraId).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('CAMERA_MODEL_DIALOG.TOASTS.DELETE_SUCCESS') as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((camera) =>
            ProjectLocationPlanCameraActions.deleteCameraSuccess(
              { camera },
            ),
          ),
          catchError(() => {
            toastService.showCustomAlert(
              translateService.instant('CAMERA_MODEL_DIALOG.TOASTS.DELETE_FAIL') as string,
              MsgBoxType.SUCCESS,
            );
            return of(ProjectLocationPlanCameraActions.deleteCameraFail())
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const bulkDeleteProjectLocationPlanCameras = createEffect(
  (
    actions$ = inject(Actions),
    cameraService = inject(ProjectLocationPlanCameraService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCameraActions.bulkDeleteProjectLocationPlanCameras),
      switchMap(({ projectId, cameraIds }) => {
        return cameraService.bulkDelete(projectId, cameraIds).pipe(
          map(() =>
            ProjectLocationPlanCameraActions.bulkDeleteProjectLocationPlanCamerasSuccess({
              cameraIds,
            }),
          ),
          catchError(() => {
            return of(
              ProjectLocationPlanCameraActions.bulkDeleteProjectLocationPlanCamerasFail(),
            );
          }),
        );
      }),
    );
  },
  { functional: true },
);

export const bulkCreateCameras = createEffect(
  (
    actions$ = inject(Actions),
    cameraService = inject(ProjectLocationPlanCameraService),
  ) => {
    return actions$.pipe(
      ofType(ProjectLocationPlanCameraActions.bulkCreateCameras),
      switchMap(({ projectId, cameras }) => {
        return cameraService.bulkCreate(projectId, cameras).pipe(
          map((cameras) =>
            ProjectLocationPlanCameraActions.bulkCreateCamerasSuccess({
              cameras,
            }),
          ),
          catchError(() => {
            return of(
              ProjectLocationPlanCameraActions.bulkCreateCamerasFail(),
            );
          }),
        );
      }),
    );
  },
  { functional: true },
);
